import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import RefreshIcon from '@mui/icons-material/Refresh';
import dayjs from 'dayjs';
import { DataGrid, GridColDef, GridToolbar } from '@mui/x-data-grid';
import type { GridRenderCellParams } from '@mui/x-data-grid';
import { plPL } from '@mui/x-data-grid/locales';
import timeTrackerController, { ITimeTrackerScan } from '../../../Api/TimeTracker';
import { useLocalStorageState } from '../utils/useLocalStorageState';

type TimeTrackerRow = ITimeTrackerScan & {
  id: string;
  createdAtDate: Date | null;
};

const chipColorForPhase = (phase?: string | null) => {
  const key = (phase || '').toLowerCase();
  if (key === 'start') return 'success' as const;
  if (key === 'end') return 'info' as const;
  if (key === 'blocked') return 'warning' as const;
  return 'default' as const;
};

const TodayScansView: FC = () => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const [scans, setScans] = useState<ITimeTrackerScan[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deletingScanId, setDeletingScanId] = useState<string | null>(null);

  const [personFilter, setPersonFilter] = useState<string>('');
  const [activityFilter, setActivityFilter] = useState<string>('');

  const [rowHeight, setRowHeight] = useLocalStorageState<number>('timeTracker:today:rowHeight', 44);

  const fetchToday = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    const from = dayjs().startOf('day').toISOString();
    const to = dayjs().endOf('day').toISOString();

    const res = await timeTrackerController.getAllScans({ from, to });
    if (!res.ok) {
      setScans([]);
      setError(res.message || 'Nie udało się pobrać skanów');
      setIsLoading(false);
      return;
    }

    setScans(res.scans || []);
    setIsLoading(false);
  }, []);

  useEffect(() => {
    fetchToday();
  }, [fetchToday]);

  type Option = { label: string; value: string };
  const persons: Option[] = useMemo(() => {
    const values = Array.from(
      new Set(scans.map((s) => s.employeeName).filter((v): v is string => Boolean(v && v.trim())))
    ).sort((a, b) => a.localeCompare(b));
    return values.map((v) => ({ label: v, value: v }));
  }, [scans]);

  const activities = useMemo(() => {
    return Array.from(
      new Set(scans.map((s) => s.activityLabel || s.activityType || '').filter(Boolean))
    ).sort((a, b) => a.localeCompare(b, 'pl'));
  }, [scans]);

  const filteredScans = useMemo(() => {
    return scans.filter((s) => {
      if (personFilter && (s.employeeName || '') !== personFilter) return false;
      const label = s.activityLabel || s.activityType || '';
      if (activityFilter && label !== activityFilter) return false;
      return true;
    });
  }, [scans, personFilter, activityFilter]);

  const rows = useMemo<TimeTrackerRow[]>(() => {
    return filteredScans.map((s) => ({
      id: s.scanId,
      ...s,
      createdAtDate: s.createdAt ? new Date(s.createdAt) : null,
    }));
  }, [filteredScans]);

  const handleDeleteScan = useCallback(
    async (scanId: string) => {
      const ok = window.confirm('Czy na pewno chcesz usunąć ten skan?');
      if (!ok) return;

      setDeletingScanId(scanId);
      setError(null);

      const res = await timeTrackerController.deleteScan(scanId);
      if (!res.ok) {
        setError(res.message || 'Nie udało się usunąć skanu');
        setDeletingScanId(null);
        return;
      }

      setScans((prev) => prev.filter((s) => s.scanId !== scanId));
      setDeletingScanId(null);
    },
    []
  );

  const columns: GridColDef<TimeTrackerRow>[] = useMemo(
    () => [
      { field: 'createdAtDate', headerName: 'createdAt', type: 'dateTime', width: 200, hideable: true },
      {
        field: 'employeeName',
        headerName: 'Pracownik',
        flex: 1.2,
        minWidth: 220,
        valueGetter: (_value: unknown, row: TimeTrackerRow) => row.employeeName || 'Nieprzypisana karta',
      },
      {
        field: 'createdAt',
        headerName: 'Data',
        width: 120,
        valueGetter: (_value: unknown, row: TimeTrackerRow) =>
          row.createdAt ? dayjs(row.createdAt).format('YYYY-MM-DD') : '-',
      },
      { field: 'time', headerName: 'Godzina', width: 110 },
      {
        field: 'activityLabel',
        headerName: 'Aktywność',
        width: 160,
        valueGetter: (_value: unknown, row: TimeTrackerRow) => row.activityLabel || row.activityType || '-',
      },
      {
        field: 'actionPhase',
        headerName: 'Start/Stop',
        width: 130,
        renderCell: (params: GridRenderCellParams<TimeTrackerRow, string | null>) => (
          <Chip
            size="small"
            variant="filled"
            color={chipColorForPhase(params.value)}
            label={params.value || '-'}
          />
        ),
      },
      {
        field: 'duration',
        headerName: 'Czas',
        width: 110,
        valueGetter: (_value: unknown, row: TimeTrackerRow) => (row.actionPhase === 'end' ? row.duration || '-' : '-'),
      },
      { field: 'uid', headerName: 'UID', width: 180, sortable: false },
      {
        field: 'delete',
        headerName: 'X',
        width: 70,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params: GridRenderCellParams<TimeTrackerRow>) => (
          <Button
            variant="text"
            color="error"
            size="small"
            onClick={() => handleDeleteScan(params.row.scanId)}
            disabled={deletingScanId === params.row.scanId}
          >
            X
          </Button>
        ),
      },
    ],
    [deletingScanId, handleDeleteScan]
  );

  return (
    <Box>
      <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} gap={2}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight={800}>
              Dzisiaj
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Wynik: {rows.length} / {scans.length}
            </Typography>
          </Box>

          <Button
            variant="contained"
            startIcon={isLoading ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon />}
            onClick={fetchToday}
            disabled={isLoading}
            fullWidth={!isDesktop}
          >
            Odśwież
          </Button>
        </Stack>

        {error ? (
          <Box sx={{ mt: 2 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        ) : null}

        <Stack direction={{ xs: 'column', md: 'row' }} gap={1.5} sx={{ mt: 2 }}>
          <Autocomplete
            options={persons}
            value={persons.find((p) => p.value === personFilter) || null}
            onChange={(_, val) => setPersonFilter(val?.value || '')}
            renderInput={(params) => <TextField {...params} label="Pracownik" placeholder="Wybierz…" />}
            sx={{ minWidth: 260, flex: 1 }}
            clearOnEscape
          />

          <TextField
            select
            label="Aktywność"
            value={activityFilter}
            onChange={(e) => setActivityFilter(e.target.value)}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">Wszystkie</MenuItem>
            {activities.map((a) => (
              <MenuItem key={a} value={a}>
                {a}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Wysokość wiersza"
            value={String(rowHeight)}
            onChange={(e) => setRowHeight(Number(e.target.value))}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="36">Mała</MenuItem>
            <MenuItem value="44">Normalna</MenuItem>
            <MenuItem value="52">Duża</MenuItem>
          </TextField>
        </Stack>

        <Box sx={{ mt: 2 }}>
          <DataGrid
            rows={rows}
            columns={columns}
            autoHeight
            rowHeight={rowHeight}
            columnHeaderHeight={isDesktop ? 52 : 56}
            disableRowSelectionOnClick
            slots={{ toolbar: GridToolbar }}
            localeText={plPL.components.MuiDataGrid.defaultProps.localeText}
            initialState={{
              sorting: { sortModel: [{ field: 'createdAtDate', sort: 'desc' }] },
              columns: { columnVisibilityModel: { createdAtDate: false } },
            }}
            sx={{ borderRadius: 2, borderColor: 'divider' }}
          />
        </Box>
      </Paper>
    </Box>
  );
};

export default TodayScansView;
